import {
  type ChatModelId,
  estimateTurnCost,
  formatCredits,
} from "@models/billing";
import BoltIcon from "@mui/icons-material/BoltRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Box, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useWallet } from "../hooks/useBilling";

interface CreditEstimateProps {
  draftChars: number;
  model: ChatModelId;
}

const MODEL_LABEL: Record<ChatModelId, string> = {
  sonnet: "Sonnet 4.6",
  opus: "Opus 4.7",
};

/**
 * Inline credit estimate for the chat composer.
 *
 * Updates as the user types. Tooltip explains it's an estimate; a low-
 * funds state shifts the colour to warning and the icon to a triangle.
 * Click navigates to billing — same target as the sidebar pill so the
 * mental model stays consistent.
 *
 * The compose-side gate (`hasFunds`) is exposed via `useEstimateGate`
 * below so the parent can disable the send button.
 */
export function CreditEstimate({ draftChars, model }: CreditEstimateProps) {
  const { data: wallet } = useWallet();
  const navigate = useNavigate();

  const balance = wallet?.balance;
  // Floor: even an empty composer should show what *one* substantive
  // turn costs, so the user can plan ahead. Fall back to 200 chars when
  // the draft is empty so the number isn't a useless "1 credit".
  const draft = draftChars > 0 ? draftChars : 200;
  const credits = estimateTurnCost(draft, model);

  const lowFunds = balance != null && balance < credits;

  const tooltip = lowFunds
    ? `Need ~${credits} credits, you have ${formatCredits(balance ?? 0)}. Click to top up.`
    : `Estimated cost for the next turn — ~${credits} credits on ${MODEL_LABEL[model]}. Final amount settles after the response.`;

  return (
    <Tooltip title={tooltip} placement="top">
      <Box
        component="button"
        type="button"
        onClick={() => void navigate({ to: "/settings/billing" })}
        aria-label={
          lowFunds
            ? `Low credit warning: need ${credits}, have ${formatCredits(balance ?? 0)}. Open billing.`
            : `Estimated turn cost ${credits} credits. Open billing.`
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          background: "transparent",
          border: "1px solid transparent",
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          cursor: "pointer",
          font: "inherit",
          color: lowFunds ? "warning.main" : "text.disabled",
          fontSize: "0.7rem",
          fontWeight: 500,
          transition: (t) =>
            `color ${t.motion.fast}, border-color ${t.motion.fast}`,
          "&:hover, &:focus-visible": {
            color: lowFunds ? "warning.light" : "text.secondary",
            borderColor: "border.default",
          },
          "&:focus-visible": { outline: "none" },
        }}
      >
        {lowFunds ? (
          <WarningRoundedIcon sx={{ fontSize: 12 }} />
        ) : (
          <BoltIcon sx={{ fontSize: 12 }} />
        )}
        <Typography
          component="span"
          sx={{
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.7rem",
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          ~{credits} credits
        </Typography>
        <Box component="span" sx={{ color: "text.disabled" }}>
          ·
        </Box>
        <Typography
          component="span"
          sx={{ fontSize: "0.7rem", fontWeight: 500 }}
        >
          {MODEL_LABEL[model]}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * Compose-side gate. Returns `true` when the user has enough credits
 * for the estimated cost. While the wallet is loading this returns
 * `true` so we don't gate the send button on a transient null balance.
 */
export function useEstimateGate(
  draftChars: number,
  model: ChatModelId,
): { hasFunds: boolean; estimate: number } {
  const { data: wallet } = useWallet();
  const draft = draftChars > 0 ? draftChars : 200;
  const estimate = estimateTurnCost(draft, model);
  const balance = wallet?.balance;
  // While loading or unauthenticated, don't gate — the server still
  // enforces the real check, so this is purely a friendlier UX.
  const hasFunds = balance == null ? true : balance >= estimate;
  return { hasFunds, estimate };
}
